import React, { ComponentProps, useCallback, useMemo, useState } from 'react';
import SVG from 'react-inlinesvg';
import {
  AlertGroupProvider,
  OfferingCard,
  useFeature,
} from '@ole-ui/ole-ui-components';
import { useTranslation } from 'react-i18next';
import { isBoolean } from 'lodash';
import { EnablementFeatureFlags } from 'config/featureFlags';
import { PremiumSessionUserFacingStatus } from 'types/premiumSession';
import { CatalogEntry, Modality } from 'types';
import useMobxStores from 'hooks/useMobxStores';
import { observer } from 'mobx-react';
import { AlertVariant } from '@patternfly/react-core';
import { useOfferingCardContext } from '../OfferingCardContext';
import { ModalityExtension } from '../utils/types';

import '../styles.scss';
import { courseIconConfig } from '../configs/course';
import offeringTypeRegistry from '../configs';

type HeaderBuilderProps = {
  children?: React.ReactNode;
  displaySecondIcon?: boolean;
} & ComponentProps<typeof OfferingCard.Header>;

const HeaderBuilder = ({
  children,
  displaySecondIcon,
  ...offeringCardHeaderProps
}: HeaderBuilderProps) => {
  const { t } = useTranslation();
  const {
    entry,
    hasEntry,
    sessionCollateralData,
    isLocked,
    isPremiumCard,
    offeringType,
    premiumSessionSlug,
    premiumSessionStatus,
    progress,
  } = useOfferingCardContext();
  const { catalogStore } = useMobxStores();
  const { addAlert } = AlertGroupProvider.useAlertGroupContext();

  const isSkillsAssessment =
    entry?.modality === ModalityExtension.SkillsAssessment;
  const isSkillsPath = offeringType === ModalityExtension.SkillsPath;
  const canSaveForLater = !isSkillsAssessment && !isSkillsPath;
  const savedForLaterCardsEnabled =
    useFeature({
      section: 'enablements',
      flagName: EnablementFeatureFlags.SavedForLaterCardsEnabled,
      defaultFlagValue: false,
    }) && canSaveForLater;

  const { offeringCode, offeringModality } = useMemo(() => {
    if (hasEntry && canSaveForLater) {
      const catalogEntry = entry as CatalogEntry;
      return {
        offeringCode: catalogEntry.code,
        offeringModality: catalogEntry.modality,
      };
    }
    return {
      offeringCode: sessionCollateralData?.offering_slug.split('-s')?.[0] || '',
      offeringModality: Modality.Course,
    };
  }, [hasEntry, canSaveForLater, entry, sessionCollateralData]);

  const isSavedForLaterCard = catalogStore.isSavedForLater(offeringCode);

  const [isLoading, setIsLoading] = useState(false);

  const handleSavedForLaterCard = useCallback(async () => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);
    try {
      // Toggle: ON upserts with a fresh timestamp (so in-progress offerings leave
      // Continue learning); OFF removes via the existing favorites toggle.
      await catalogStore.postSavedForLater(
        offeringCode,
        offeringModality,
        !isSavedForLaterCard,
      );
    } catch {
      addAlert({
        key: `error-saved-for-later-${offeringCode}`,
        variant: AlertVariant.warning,
        title: t(
          "Couldn't save for later. Please check your connection and try again.",
        ),
      });
    }
    setIsLoading(false);
  }, [
    addAlert,
    catalogStore,
    isLoading,
    isSavedForLaterCard,
    offeringCode,
    offeringModality,
    t,
  ]);

  const iconConfig = useMemo(() => {
    if (!offeringType) return {};

    const config = offeringTypeRegistry?.[offeringType];

    const { title, iconA, iconB } = config
      ? config.headerConfig({ premiumSessionStatus, t })
      : {
          iconA: undefined,
          iconB: courseIconConfig,
          title: t('Offering'),
        };

    let headerTitle = title;
    let iconBComponent = (
      <SVG
        src={iconB.src}
        title={iconB.iconTitle}
        style={{ color: 'color' in iconB && !isLocked ? iconB.color : '#fff' }}
      />
    );

    if (offeringType === ModalityExtension.PremiumSession) {
      // This regex extract session number from slug
      const sessionNumber = premiumSessionSlug?.match(/-s0?(\d+)$/)?.[1] ?? '0';
      headerTitle = t('Session {{sessionNumber}}', { sessionNumber });

      if (premiumSessionStatus === PremiumSessionUserFacingStatus.Schedulable) {
        iconBComponent = (
          <div className="session-icon-container">
            <span>{sessionNumber}</span>
          </div>
        );
      }
    }

    return {
      ...(!!iconA && {
        headerIconA: {
          backgroundColor: isLocked
            ? 'var(--rhls-color-ws-cancel-dark)'
            : iconA.backgroundColor,
          icon: (
            <SVG
              src={iconA.src}
              title={iconA.iconTitle}
              style={{
                color: 'color' in iconA && !isLocked ? iconA.color : '#fff',
              }}
            />
          ),
        },
      }),
      headerIconB: {
        backgroundColor: isLocked
          ? 'var(--rhls-color-ws-cancel-dark)'
          : iconB.backgroundColor,
        icon: iconBComponent,
      },
      title: headerTitle,
    };
  }, [
    displaySecondIcon,
    entry,
    offeringType,
    premiumSessionStatus,
    premiumSessionSlug,
    t,
  ]);

  const savedForLaterButtonProps = useMemo(() => {
    if (savedForLaterCardsEnabled && isBoolean(isSavedForLaterCard)) {
      return {
        dataAnalyticsId: `offering-card-favorite-${offeringCode}`,
        onClick: handleSavedForLaterCard,
        isSavedForLater: isSavedForLaterCard,
        isLoading,
      };
    }
    return undefined;
  }, [
    savedForLaterCardsEnabled,
    handleSavedForLaterCard,
    offeringCode,
    isSavedForLaterCard,
    isLoading,
  ]);

  const displayProgressBar = !isPremiumCard && !isLocked;

  return (
    <OfferingCard.Header
      className="offering-card-builder__header"
      savedForLaterButtonProps={savedForLaterButtonProps}
      progress={displayProgressBar ? progress : 0}
      {...iconConfig}
      {...offeringCardHeaderProps}
    >
      {children}
    </OfferingCard.Header>
  );
};

export default observer(HeaderBuilder);
